import { css } from '@microsoft/fast-element';
import { getToken } from '../../design-tokens';
import { display } from '../../foundation/display';
import { variantBehavior } from '../../styles/behaviors';

export type BadgeVariant = 'filled' | 'outline' | 'ghost';
export type BadgeColor = 'neutral' | 'primary' | 'secondary' | 'success' | 'error' | 'warning';

export interface IBadgeStyles {
    variant: BadgeVariant;
    color: BadgeColor;
}

export const badgeStyles = css<IBadgeStyles>`
    ${display('inline-flex')}

    :host {
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        padding: calc(${getToken('space-design-unit')} * 0.5px) calc(${getToken('space-design-unit')} * 2px);
        border-radius: calc(${getToken('space-button-corner-radius')} * 1px);
        font-size: ${getToken('typography-body-x-small-font-size')};
        line-height: ${getToken('typography-body-x-small-line-height')};
        font-weight: 600;
        white-space: nowrap;
        border: calc(${getToken('space-stroke-width')} * 1px) solid transparent;
        cursor: default;
        user-select: none;
    }
`.withBehaviors(
    variantBehavior('filled', FilledBadgeStyles()),
    variantBehavior('outline', OutlineBadgeStyles()),
    variantBehavior('ghost', GhostBadgeStyles()),
);

export function FilledBadgeStyles() {
    return css`
        :host([color=neutral]) {
            background: ${getToken('color-neutral-fill')};
            border-color: ${getToken('color-neutral-fill')};
            color: ${getToken('color-neutral-text')};
        }

        :host([color=primary]) {
            background: ${getToken('color-primary-base')};
            border-color: ${getToken('color-primary-base')};
            color: #ffffff;
        }

        :host([color=secondary]) {
            background: ${getToken('color-secondary-base')};
            border-color: ${getToken('color-secondary-base')};
            color: #ffffff;
        }

        :host([color=success]) {
            background: ${getToken('color-success')};
            border-color: ${getToken('color-success')};
            color: #ffffff;
        }

        :host([color=error]) {
            background: ${getToken('color-error')};
            border-color: ${getToken('color-error')};
            color: #ffffff;
        }

        :host([color=warning]) {
            background: #f59e0b;
            border-color: #f59e0b;
            color: #1c1917;
        }
    `;
}

export function OutlineBadgeStyles() {
    return css`
        :host {
            background: transparent;
        }

        :host([color=neutral]) {
            border-color: ${getToken('color-neutral-text')};
            color: ${getToken('color-neutral-text')};
        }

        :host([color=primary]) {
            border-color: ${getToken('color-primary-base')};
            color: ${getToken('color-primary-base')};
        }

        :host([color=secondary]) {
            border-color: ${getToken('color-secondary-base')};
            color: ${getToken('color-secondary-base')};
        }

        :host([color=success]) {
            border-color: ${getToken('color-success')};
            color: ${getToken('color-success')};
        }

        :host([color=error]) {
            border-color: ${getToken('color-error')};
            color: ${getToken('color-error')};
        }

        :host([color=warning]) {
            border-color: #d97706;
            color: #d97706;
        }
    `;
}

export function GhostBadgeStyles() {
    return css`
        :host {
            background: transparent;
            border-color: transparent;
        }

        :host([color=neutral]) {
            color: ${getToken('color-neutral-text')};
        }

        :host([color=primary]) {
            color: ${getToken('color-primary-base')};
        }

        :host([color=secondary]) {
            color: ${getToken('color-secondary-base')};
        }

        :host([color=success]) {
            color: ${getToken('color-success')};
        }

        :host([color=error]) {
            color: ${getToken('color-error')};
        }

        :host([color=warning]) {
            color: #d97706;
        }
    `;
}
